import type { CardCandidate, ScanResult } from './types'

type Region = {
  x: number
  y: number
  width: number
  height: number
}

type SymbolDescriptor = {
  grayscale: number[]
}

type ScoredCandidate = {
  candidate: CardCandidate
  index: number
  score: number
}

const descriptorWidth = 31
const descriptorHeight = 31
const printingMatchTimeoutMs = 3501
const setSymbolRegion: Region = {
  x: 0.78,
  y: 0.49,
  width: 0.19,
  height: 0.14,
}

export async function rankMtgPrintingsBySetDetails(
  capturedCard: HTMLCanvasElement,
  candidates: CardCandidate[],
  scanResult: ScanResult,
  onProgress: (label: string) => void,
): Promise<CardCandidate[]> {
  if (
    candidates.length < 2 ||
    !capturedCard.width ||
    !capturedCard.height
  ) {
    return candidates
  }

  const metadataScores = candidates.map((candidate, index) => ({
    candidate,
    index,
    score: getMetadataScore(candidate, scanResult),
  }))
  const bestMetadataScore = Math.max(...metadataScores.map(({ score }) => score))

  if (bestMetadataScore > 0) {
    onProgress('Matched release details')
    return sortCandidates(metadataScores, candidates)
  }

  onProgress('Checking the release symbol')
  const sourceDescriptor = createSymbolDescriptor(capturedCard)
  const limitedCandidates = candidates.slice(0, 9)
  const scored = await Promise.all(
    limitedCandidates.map(async (candidate, index) => {
      const image = await loadCandidateImage(candidate.imageUrl)

      if (!image) {
        return { candidate, index, score: -1 }
      }

      try {
        return {
          candidate,
          index,
          score: descriptorSimilarity(
            sourceDescriptor,
            createSymbolDescriptor(image),
          ),
        }
      } finally {
        image.close()
      }
    }),
  )
  const ranked = [...scored].sort(
    (left, right) => right.score - left.score || left.index - right.index,
  )
  const best = ranked[0]
  const runnerUp = ranked[1]

  if (
    !best ||
    best.score < 0.63 ||
    (runnerUp && best.score - runnerUp.score < 0.025)
  ) {
    return candidates
  }

  return sortCandidates(scored, candidates)
}

function sortCandidates(
  scored: ScoredCandidate[],
  candidates: CardCandidate[],
): CardCandidate[] {
  const ranked = [...scored].sort(
    (left, right) => right.score - left.score || left.index - right.index,
  )
  const rankedIds = new Set(ranked.map(({ candidate }) => candidate.sourceId))

  return [
    ...ranked.map(({ candidate }) => candidate),
    ...candidates.filter((candidate) => !rankedIds.has(candidate.sourceId)),
  ]
}

function getMetadataScore(
  candidate: CardCandidate,
  scanResult: ScanResult,
): number {
  const setCode = normalizeMetadata(candidate.setCode)
  const collectorNumber = normalizeCollectorNumber(candidate.collectorNumber)
  let score = 0

  if (
    collectorNumber &&
    scanResult.collectorNumbers.some(
      (value) => normalizeCollectorNumber(value) === collectorNumber,
    )
  ) {
    score += 2
  }

  if (
    setCode &&
    scanResult.setCodes.some((value) => normalizeMetadata(value) === setCode)
  ) {
    score += 1
  }

  return score
}

function createSymbolDescriptor(source: CanvasImageSource): SymbolDescriptor {
  const sourceSize = getSourceSize(source)
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d', { willReadFrequently: true })

  canvas.width = descriptorWidth
  canvas.height = descriptorHeight

  if (!context) {
    return { grayscale: Array(descriptorWidth * descriptorHeight).fill(0) }
  }

  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.filter = 'grayscale(1) contrast(1.35)'
  context.drawImage(
    source,
    Math.round(sourceSize.width * setSymbolRegion.x),
    Math.round(sourceSize.height * setSymbolRegion.y),
    Math.max(1, Math.round(sourceSize.width * setSymbolRegion.width)),
    Math.max(1, Math.round(sourceSize.height * setSymbolRegion.height)),
    0,
    0,
    descriptorWidth,
    descriptorHeight,
  )

  const data = context.getImageData(
    0,
    0,
    descriptorWidth,
    descriptorHeight,
  ).data
  const grayscale: number[] = []

  for (let index = 0; index < data.length; index += 4) {
    grayscale.push(
      data[index] * 0.299 +
      data[index + 1] * 0.587 +
      data[index + 2] * 0.114,
    )
  }

  return { grayscale }
}

function descriptorSimilarity(
  left: SymbolDescriptor,
  right: SymbolDescriptor,
): number {
  const leftAverage = average(left.grayscale)
  const rightAverage = average(right.grayscale)
  let hashMatches = 0
  let horizontalEdgeMatches = 0
  let verticalEdgeMatches = 0
  let horizontalEdges = 0
  let verticalEdges = 0

  for (let index = 0; index < left.grayscale.length; index += 1) {
    if (
      (left.grayscale[index] >= leftAverage) ===
      (right.grayscale[index] >= rightAverage)
    ) {
      hashMatches += 1
    }

    if ((index + 1) % descriptorWidth !== 0) {
      const leftEdge = left.grayscale[index + 1] >= left.grayscale[index]
      const rightEdge = right.grayscale[index + 1] >= right.grayscale[index]
      horizontalEdgeMatches += leftEdge === rightEdge ? 1 : 0
      horizontalEdges += 1
    }

    if (index + descriptorWidth < left.grayscale.length) {
      const leftEdge =
        left.grayscale[index + descriptorWidth] >= left.grayscale[index]
      const rightEdge =
        right.grayscale[index + descriptorWidth] >= right.grayscale[index]
      verticalEdgeMatches += leftEdge === rightEdge ? 1 : 0
      verticalEdges += 1
    }
  }

  return (
    (hashMatches / Math.max(1, left.grayscale.length)) * 0.4 +
    (horizontalEdgeMatches / Math.max(1, horizontalEdges)) * 0.3 +
    (verticalEdgeMatches / Math.max(1, verticalEdges)) * 0.3
  )
}

function average(values: number[]): number {
  return (
    values.reduce((total, value) => total + value, 0) /
    Math.max(1, values.length)
  )
}

async function loadCandidateImage(imageUrl: string): Promise<ImageBitmap | null> {
  if (!imageUrl) {
    return null
  }

  const controller = new AbortController()
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    printingMatchTimeoutMs,
  )

  try {
    const response = await fetch(getComparisonImageUrl(imageUrl), {
      signal: controller.signal,
    })

    if (!response.ok) {
      return null
    }

    return await createImageBitmap(await response.blob())
  } catch {
    return null
  } finally {
    window.clearTimeout(timeoutId)
  }
}

function getComparisonImageUrl(imageUrl: string): string {
  return imageUrl.replace('/normal/', '/small/')
}

function getSourceSize(source: CanvasImageSource): {
  height: number
  width: number
} {
  if (source instanceof HTMLCanvasElement) {
    return { height: source.height, width: source.width }
  }

  if (source instanceof ImageBitmap) {
    return { height: source.height, width: source.width }
  }

  if (source instanceof HTMLImageElement) {
    return {
      height: source.naturalHeight || source.height,
      width: source.naturalWidth || source.width,
    }
  }

  return { height: 1, width: 1 }
}

function normalizeMetadata(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

function normalizeCollectorNumber(value: string): string {
  return normalizeMetadata(value).replace(/^0+/, '')
}
